// @mui
import {
  AppBar,
  Box,
  IconButton,
  Stack,
  Toolbar,
  useTheme,
} from '@mui/material';
// utils
import { bgBlur } from '../../../utils/cssStyles';
// hooks
import { useResponsiveBreakpoints } from 'src/hooks/ueResponsiveBreakpoints';
import useOffSetTop from '../../../hooks/useOffSetTop';
import useResponsive from '../../../hooks/useResponsive';
// config
import Iconify from '../../../components/iconify';
import { useSettingsContext } from '../../../components/settings';
import { HEADER, NAV } from '../../../config-global';
//
import { useLocation } from 'react-router-dom';
import { useUserContext } from 'src/auth/useUserContext';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { Roles } from 'src/types/User';
import Image from '../../../components/image';
import { PATH_DASHBOARD } from '../../../routes/paths';
import AccountPopover from './AccountPopover';
import OrganizationPopover from './OrganizationPopover';
// ----------------------------------------------------------------------

type Props = {
  onOpenNav?: VoidFunction;
  isProSidebar?: boolean;
};

export default function Header({ onOpenNav, isProSidebar }: Props) {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { isUserRole, isOnboarded } = useUserContext();
  const {
    isOrganizationAdminWithoutOrganizations,
    isOrganizationNotVerified,
    selectedRole,
    organizations,
  } = useOrganizationContext();

  const { themeLayout } = useSettingsContext();
  const { isLg } = useResponsiveBreakpoints();
  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const isOffset = useOffSetTop(HEADER.H_DASHBOARD_DESKTOP) && !isNavHorizontal;

  const getLogoUrl = () => {
    if (
      selectedRole === Roles.organization_admin ||
      selectedRole === Roles.organization_owner
    ) {
      return PATH_DASHBOARD.general.calendar;
    }

    if (selectedRole === Roles.professional) {
      return PATH_DASHBOARD.general.waitingRoom;
    }

    if (isUserRole) {
      return PATH_DASHBOARD.general.profiles.root;
    }
    return PATH_DASHBOARD.root;
  };

  const renderContent = (
    <>
      {!isLg && (
        <Stack
          sx={{ cursor: 'pointer' }}
          justifyContent="center"
          alignItems="center"
        >
          <Image
            disabledEffect
            visibleByDefault
            onClick={() => window.location.replace(getLogoUrl())}
            alt="Vetsi Logo"
            src={
              isUserRole
                ? '/assets/logo/logo_dashboard.svg'
                : '/assets/logo/vetsi-clinic-logo.svg'
            }
          />
        </Stack>
      )}

      {!isProSidebar &&
        isLg &&
        !isOrganizationAdminWithoutOrganizations &&
        !isOrganizationNotVerified && (
          <IconButton onClick={onOpenNav} sx={{ mr: 1, color: 'text.primary' }}>
            <Iconify icon="eva:menu-2-fill" />
          </IconButton>
        )}

      <Stack
        flexGrow={1}
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={{ xs: 0.5, sm: 1.5 }}
      >
        {isOnboarded && !!organizations.length && <OrganizationPopover />}
        <AccountPopover />
      </Stack>
    </>
  );

  return (
    <>
      <AppBar
        sx={{
          boxShadow:
            pathname === PATH_DASHBOARD.general.updateAccount ||
            isUserRole ||
            isProSidebar
              ? theme.customShadows.z8
              : 'none',
          height: HEADER.H_MOBILE,
          zIndex: theme.zIndex.appBar + 1,
          ...bgBlur({
            color: theme.palette.background.default,
          }),
          transition: theme.transitions.create(['height', 'width'], {
            duration: theme.transitions.duration.shorter,
          }),
          ...(isDesktop && {
            width: `calc(100% - ${NAV.W_DASHBOARD + 1}px)`,
            height: HEADER.H_DASHBOARD_DESKTOP,
            ...(isOffset && {
              height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            }),
            ...(isNavHorizontal && {
              width: 1,
              bgcolor: 'background.default',
              height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            }),
            ...(isNavMini && {
              width: `calc(100% - ${NAV.W_DASHBOARD_MINI + 1}px)`,
            }),
            ...(isProSidebar && {
              width: 1,
              bgcolor: 'background.default',
              height: HEADER.H_DASHBOARD_DESKTOP_OFFSET,
            }),
          }),
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', flex: 1 }}>
          <Toolbar
            sx={{
              width: 1,
              minHeight: '56px !important',

              '@media (max-width:1108px)': { px: 2 },
              '@media (min-width:1108px)': { px: 2 },
            }}
          >
            {renderContent}
          </Toolbar>
        </Box>
      </AppBar>
    </>
  );
}
